import * as Dialog from '@radix-ui/react-dialog';
import type { NotificationList, NotificationView } from '@homecare/contracts';
import { EmptyState, Spinner } from '@homecare/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Bell } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useApi } from '../../lib/api-hooks';

/**
 * The notification bell (§12, §17.2).
 *
 * Reads `GET /v1/notifications` — the caller's own, and only theirs. Today the
 * only thing that writes one is the nightly credential job, which tells
 * whoever holds `credential:verify` that a caregiver's credential is about to
 * lapse or has. Opening one takes that person to the caregiver's record, where
 * the credential is, and marks it read.
 *
 * The count appears only when something is unread. A badge on an empty list is
 * a lie with a number on it, and a zero is a badge on an empty list.
 */

const REFRESH_MS = 60_000;

const when = (iso: string): string =>
  new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(iso));

export function NotificationBell(): ReactNode {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const inbox = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<NotificationList>('/v1/notifications'),
    // The job runs once a night per agency; a minute is generous, and the
    // bell refetches whenever it is opened anyway.
    refetchInterval: REFRESH_MS,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => api.send<NotificationView>('POST', `/v1/notifications/${id}/read`),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const unread = inbox.data?.unread ?? 0;
  const notifications = inbox.data?.notifications ?? [];

  const openOne = (notification: NotificationView): void => {
    if (notification.readAt === null) markRead.mutate(notification.id);
    setOpen(false);
    void navigate({
      to: '/caregivers/$caregiverId',
      params: { caregiverId: notification.subject.caregiverId },
    });
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) void inbox.refetch();
      }}
    >
      <Dialog.Trigger asChild>
        <button
          type="button"
          aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
          className="text-ink-2 hover:bg-surface-2 hover:text-ink min-h-web-primary relative grid w-11 place-items-center rounded-md"
        >
          <Bell aria-hidden="true" className="h-5 w-5" />
          {unread > 0 && (
            <span
              aria-hidden="true"
              className="bg-critical text-ink-inv absolute right-1 top-1 grid min-w-5 place-items-center rounded-full px-1 text-xs font-bold leading-5"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="bg-overlay fixed inset-0" />
        <Dialog.Content className="border-line bg-surface shadow-e4 fixed right-4 top-16 grid max-h-[min(36rem,calc(100vh-6rem))] w-[min(26rem,calc(100vw-2rem))] grid-rows-[auto_1fr_auto] gap-3 rounded-lg border p-5">
          <Dialog.Title className="text-ink text-lg font-bold tracking-tight">
            Notifications
          </Dialog.Title>
          <Dialog.Description className="sr-only">
            Credentials about to run out or already expired, for the caregivers you verify.
          </Dialog.Description>

          <div className="min-h-0 overflow-y-auto">
            {inbox.isPending ? (
              <p className="text-ink-2 flex items-center gap-2 py-6 text-sm" role="status">
                <Spinner /> Loading your notifications…
              </p>
            ) : inbox.isError ? (
              <ErrorNote
                error={inbox.error}
                nextStep={
                  <button
                    type="button"
                    onClick={() => void inbox.refetch()}
                    className="text-brand hover:text-brand-hover font-medium"
                  >
                    Try again
                  </button>
                }
              />
            ) : notifications.length === 0 ? (
              <EmptyState
                title="Nothing yet"
                explanation="When a caregiver's licence or certificate is about to run out, or has, it lands here — with a link to their record. Nothing is waiting on you right now."
              />
            ) : (
              <ul className="grid gap-2" aria-label="Your notifications">
                {notifications.map((notification) => (
                  <li key={notification.id}>
                    <button
                      type="button"
                      onClick={() => openOne(notification)}
                      className="border-line hover:bg-surface-2 grid w-full gap-1 rounded-md border p-3 text-left"
                    >
                      <span className="flex items-start justify-between gap-3">
                        <span
                          className={`text-ink text-sm ${notification.readAt === null ? 'font-bold' : 'font-medium'}`}
                        >
                          {notification.title}
                        </span>
                        {notification.readAt === null && <span className="sr-only">Unread.</span>}
                        <span className="text-ink-3 shrink-0 text-xs">
                          {when(notification.createdAt)}
                        </span>
                      </span>
                      <span className="text-ink-2 text-sm">{notification.body}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Dialog.Close asChild>
            <button
              type="button"
              className="text-brand hover:text-brand-hover justify-self-start text-sm font-medium"
            >
              Close
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
